import * as React from 'react';
import { Card, CardContent, CardHeader, CardLabel } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Icon } from '@phosphor-icons/react';

/** DS icon tile: tinted fill + matching icon + hairline inset ring. */
const ICON_TILE =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-primary/10 text-primary ring-1 ring-inset ring-black/[0.06]';

interface SettingsCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Uppercase label for the card header */
  label: string;
  /** Optional subtitle below the label */
  subtitle?: string;
  /** Optional Phosphor icon — rendered in a DS icon tile */
  icon?: Icon;
  /** Optional action element for the header (right side) */
  headerAction?: React.ReactNode;
}

/**
 * SettingsCard — consistent settings surface.
 * Uses Card size="comfortable" + CardLabel anatomy.
 */
const SettingsCard = React.forwardRef<HTMLDivElement, SettingsCardProps>(
  ({ className, label, subtitle, icon: IconComponent, headerAction, children, ...props }, ref) => (
    <Card ref={ref} size="comfortable" className={cn('flex flex-col', className)} {...props}>
      <CardHeader className="flex shrink-0 flex-row items-start justify-between gap-3 space-y-0">
        <div className="flex min-w-0 items-start gap-3">
          {IconComponent && (
            <div className={ICON_TILE}>
              <IconComponent className="h-5 w-5" />
            </div>
          )}
          <div data-slot="card-header" className="min-w-0">
            <CardLabel>{label}</CardLabel>
            {subtitle && <CardLabel variant="subtitle">{subtitle}</CardLabel>}
          </div>
        </div>
        {headerAction && <div className="shrink-0">{headerAction}</div>}
      </CardHeader>

      <CardContent className="flex-1">{children}</CardContent>
    </Card>
  ),
);
SettingsCard.displayName = 'SettingsCard';

interface SettingsCardSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether to show the divider above this section */
  showDivider?: boolean;
}

const SettingsCardSection = React.forwardRef<HTMLDivElement, SettingsCardSectionProps>(
  ({ className, showDivider = true, children, ...props }, ref) => (
    <div ref={ref} className={className} {...props}>
      {showDivider && <div className="my-4 border-t border-border" />}
      {children}
    </div>
  ),
);
SettingsCardSection.displayName = 'SettingsCardSection';

export { SettingsCard, SettingsCardSection, ICON_TILE as SETTINGS_ICON_TILE };
